import React, { useCallback, useMemo } from 'react';
import { Box, DropzoneFileListItem, VisualElementType } from '@cad-arc';
import { AttachmentDto } from '@mark43/cad-api';
import { useDownloadAttachment } from './hooks/use-download-attachment';
import { AttachmentDownloadInfo, CombinedMediaItem } from './types';

export interface AttachmentThumbnailItemProps {
    attachmentId: AttachmentDto['attachmentId'];
    attachmentType: AttachmentDto['attachmentType'];
    thumbnailSrc: string;
    showActionsMenu: boolean;
    fileType: CombinedMediaItem['type'];
    fileName: string;
    downloadUrl?: string;
    onOpenPreview?: (attachmentId: number) => void;
    onDelete: (attachmentId: number, attachmentType: AttachmentDto['attachmentType']) => void;
    hideDelete?: boolean;
    onDownload?: (info: AttachmentDownloadInfo) => void;
}

export function AttachmentThumbnailItem({
    attachmentId,
    attachmentType,
    thumbnailSrc,
    showActionsMenu,
    fileType,
    fileName,
    downloadUrl: fileUrl,
    onOpenPreview,
    onDelete,
    hideDelete = false,
    onDownload,
}: AttachmentThumbnailItemProps): React.ReactElement {
    const handleDownloadStart = useCallback(
        function notifyDownloadStart() {
            onDownload?.({ attachmentId, fileName });
        },
        [onDownload, attachmentId, fileName]
    );

    const { downloadAttachment } = useDownloadAttachment({
        attachmentId,
        fileName,
        fileUrl,
        onDownloadStart: handleDownloadStart,
    });

    const handleDelete = useCallback(
        function handleDeleteAttachment() {
            onDelete(attachmentId, attachmentType);
        },
        [onDelete, attachmentId, attachmentType]
    );

    const actions = useMemo(() => {
        if (!showActionsMenu) return;

        const items: {
            visual: VisualElementType;
            action: () => void;
            label: string;
        }[] = [
            {
                label: 'Download',
                visual: 'Download',
                action: downloadAttachment,
            },
        ];

        if (!hideDelete) {
            items.push({
                label: 'Delete',
                visual: 'TrashCan' as const,
                action: handleDelete,
            });
        }

        return items;
    }, [showActionsMenu, downloadAttachment, hideDelete, handleDelete]);

    return (
        <Box
            data-testid={`attachment-thumbnail-${attachmentId}`}
            onClick={() => {
                if (onOpenPreview) {
                    onOpenPreview(attachmentId);
                }
            }}
            style={{ cursor: 'pointer' }}
        >
            <DropzoneFileListItem
                variant="thumbnail"
                file={{
                    name: fileName,
                    type: fileType,
                }}
                thumbnailSrc={thumbnailSrc}
                size={showActionsMenu ? 'md' : 'sm'}
                actions={actions}
            />
        </Box>
    );
}
